import type { ComponentConfig } from '@measured/puck';
import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight, ShieldCheck, Sparkles, TrendingUp } from 'lucide-react';
import { Container } from '../layout/Container';
import { Button } from '../ui/button';
import { CountUp } from '../motion/CountUp';
import { easeStripe, easeSpring } from '../../lib/motion';

export interface HeroPremiumProps {
  eyebrow?: string;
  titleStart: string;
  titleAccent: string;
  titleEnd?: string;
  subtitle?: string;
  ctaPrimaryLabel?: string;
  ctaPrimaryHref?: string;
  ctaSecondaryLabel?: string;
  ctaSecondaryHref?: string;
  proofLabel?: string;
  proofValue?: number;
  proofSuffix?: string;
}

export function HeroPremium({
  eyebrow = 'Acteur intégré, 3 pôles, Made in France',
  titleStart = 'Travaux. Courtage énergie.',
  titleAccent = 'Mandataire CEE.',
  titleEnd = 'Un seul interlocuteur.',
  subtitle = 'Économisez en consommant moins (travaux & rénovation), en payant moins cher (comparaison de contrats gaz/élec) et en valorisant vos chantiers (mandataire CEE pour artisans RGE). Chiffres documentés, modèle économique transparent, zéro greenwashing.',
  ctaPrimaryLabel = 'Diagnostic 60 sec',
  ctaPrimaryHref = '/outils',
  ctaSecondaryLabel = 'Découvrir nos 3 pôles',
  ctaSecondaryHref = '/a-propos',
  proofLabel = 'pôles intégrés',
  proofValue = 3,
  proofSuffix = '',
}: HeroPremiumProps) {
  return (
    <section className="relative overflow-hidden bg-paper pb-28 pt-20 md:pb-36 md:pt-28">
      {/* Background Vercel/Linear : dot-grid + 1 spot glow unique */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="dot-grid-masked absolute inset-0" />
        <div className="spot-glow" />
      </div>

      {/* Hairline border bas */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent"
      />

      <Container className="relative">
        <div className="grid items-center gap-14 lg:grid-cols-[1.1fr,1fr]">
          {/* ─────────── Left — copy ─────────── */}
          <div>
            {/* Eyebrow chip Linear */}
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: easeStripe }}
              className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-white/80 px-3 py-1 text-[11px] font-medium text-stone-600 shadow-hairline backdrop-blur"
            >
              <span className="live-dot" aria-hidden />
              <span>{eyebrow}</span>
            </motion.div>

            {/* Titre — accent mono unique */}
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1, ease: easeStripe }}
              className="mt-7 text-balance font-display text-[clamp(2.5rem,5.5vw,4rem)] font-bold leading-[1.02] tracking-[-0.035em] text-ink"
            >
              <span className="block">{titleStart}</span>
              <span className="text-gradient-mono block">{titleAccent}</span>
              {titleEnd ? <span className="block text-stone-500">{titleEnd}</span> : null}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.25, ease: easeStripe }}
              className="mt-7 max-w-[34rem] text-[17px] leading-relaxed text-stone-600"
            >
              {subtitle}
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: easeStripe }}
              className="mt-9 flex flex-wrap items-center gap-2.5"
            >
              {ctaPrimaryLabel && ctaPrimaryHref ? (
                <Button asChild size="xl" variant="primary" className="group">
                  <a href={ctaPrimaryHref}>
                    {ctaPrimaryLabel}
                    <ArrowRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover/btn:translate-x-0.5" />
                  </a>
                </Button>
              ) : null}
              {ctaSecondaryLabel && ctaSecondaryHref ? (
                <Button asChild size="xl" variant="secondary" className="group">
                  <a href={ctaSecondaryHref}>
                    {ctaSecondaryLabel}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-150 ease-out group-hover/btn:-translate-y-0.5 group-hover/btn:translate-x-0.5" />
                  </a>
                </Button>
              ) : null}
            </motion.div>

            {/* Trust bar mono */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.55, ease: easeStripe }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12.5px]"
            >
              <span className="inline-flex items-center gap-2 text-stone-600">
                <ShieldCheck className="h-3.5 w-3.5 text-brand-600" strokeWidth={2} />
                <span className="font-medium text-ink">Modèle économique transparent</span>
                <span className="text-stone-400">·</span>
                <span className="text-stone-500">sans frais cachés</span>
              </span>
              <span className="hidden h-3 w-px bg-stone-200 md:inline-block" aria-hidden />
              <span className="inline-flex items-center gap-2 text-stone-600">
                <Sparkles className="h-3.5 w-3.5 text-accent-600" strokeWidth={2} />
                <span className="font-medium text-ink">Sources officielles</span>
                <span className="text-stone-400">·</span>
                <span className="text-stone-500">ADEME, fiches CEE</span>
              </span>
            </motion.div>
          </div>

          {/* ─────────── Right — Estimation card (refonte Linear) ─────────── */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 24 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: easeSpring }}
            className="relative mx-auto w-full max-w-md lg:max-w-none"
          >
            {/* Halo subtil derrière */}
            <div
              aria-hidden
              className="absolute -inset-6 -z-10 rounded-3xl bg-gradient-to-br from-brand-100/30 via-paper to-accent-100/20 blur-3xl"
            />

            {/* Live preview card — estimation type */}
            <div className="live-card p-6 md:p-8">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 border-b border-stone-100 pb-5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="live-dot" aria-hidden />
                    <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
                      Estimation type, BAT-TH-104
                    </span>
                  </div>
                  <p className="mt-1.5 font-display text-[15px] font-semibold leading-tight text-ink">
                    Hôtellerie indépendante
                  </p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-md border border-brand-200/60 bg-brand-50/80 px-2 py-1 font-mono text-[10px] font-semibold text-brand-700">
                  <TrendingUp className="h-2.5 w-2.5" strokeWidth={2.4} />
                  <span className="tabular-nums">+18%</span>
                </span>
              </div>

              {/* Big metric */}
              <div className="mt-6">
                <div className="flex items-baseline gap-2">
                  <span className="font-display text-[52px] font-bold tabular-nums leading-none tracking-[-0.035em] text-ink md:text-6xl">
                    <CountUp end={28400} />
                  </span>
                  <span className="font-display text-3xl font-semibold text-stone-500">€</span>
                </div>
                <p className="mt-2 font-mono text-[11.5px] text-stone-500">
                  prime CEE estimée, fourchette haute
                </p>
              </div>

              {/* Breakdown lines */}
              <div className="mt-7 space-y-2">
                {[
                  { label: 'Isolation toiture (R≥7)', val: '14 200 €', code: 'BAT-EN-103' },
                  { label: 'PAC air/eau 35 kW', val: '9 600 €', code: 'BAT-TH-104' },
                  { label: 'GTB classe A', val: '4 600 €', code: 'BAT-TH-116' },
                ].map((row, i) => (
                  <motion.div
                    key={row.label}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: 0.5 + i * 0.08, ease: easeStripe }}
                    className="flex items-center justify-between gap-3 rounded-md border border-stone-100 bg-stone-50/40 px-3 py-2.5 transition-colors hover:border-stone-200 hover:bg-stone-50"
                  >
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="font-mono text-[10px] font-semibold tabular-nums text-stone-400">
                        {row.code}
                      </span>
                      <span className="truncate text-[13px] font-medium text-stone-700">
                        {row.label}
                      </span>
                    </div>
                    <span className="font-mono text-[12px] font-semibold tabular-nums text-ink">
                      {row.val}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* CTA inline */}
              <a
                href="/outils"
                className="group/lnk mt-6 inline-flex items-center gap-1.5 font-mono text-[12px] font-semibold text-ink transition-colors hover:text-brand-700"
              >
                Lancer mon diagnostic en 60 secondes
                <ArrowRight className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover/lnk:translate-x-0.5" />
              </a>
            </div>

            {/* Floating proof badge */}
            <motion.div
              initial={{ opacity: 0, x: -16, y: 8 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 0.7, delay: 0.7, ease: easeStripe }}
              className="absolute -bottom-4 -left-3 hidden md:block"
            >
              <div className="flex items-center gap-2.5 rounded-full border border-stone-200 bg-white px-3 py-1.5 shadow-card">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-50 ring-1 ring-brand-200/60">
                  <span className="font-mono text-[11px] font-bold tabular-nums text-brand-700">
                    <CountUp end={proofValue} suffix={proofSuffix} />
                  </span>
                </div>
                <div className="flex flex-col leading-tight">
                  <span className="text-[11px] font-semibold text-ink">{proofLabel}</span>
                  <span className="font-mono text-[9.5px] uppercase tracking-wider text-stone-400">
                    Reporting interne
                  </span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </Container>
    </section>
  );
}

export const HeroPremiumConfig: ComponentConfig<HeroPremiumProps> = {
  fields: {
    eyebrow: { type: 'text' },
    titleStart: { type: 'text' },
    titleAccent: { type: 'text' },
    titleEnd: { type: 'text' },
    subtitle: { type: 'textarea' },
    ctaPrimaryLabel: { type: 'text' },
    ctaPrimaryHref: { type: 'text' },
    ctaSecondaryLabel: { type: 'text' },
    ctaSecondaryHref: { type: 'text' },
    proofLabel: { type: 'text' },
    proofValue: { type: 'number' },
    proofSuffix: { type: 'text' },
  },
  defaultProps: {
    eyebrow: 'Acteur intégré, 3 pôles, Made in France',
    titleStart: 'Travaux. Courtage énergie.',
    titleAccent: 'Mandataire CEE.',
    titleEnd: 'Un seul interlocuteur.',
    subtitle:
      'Économisez en consommant moins (travaux & rénovation), en payant moins cher (comparaison de contrats gaz/élec) et en valorisant vos chantiers (mandataire CEE pour artisans RGE). Chiffres documentés, modèle économique transparent, zéro greenwashing.',
    ctaPrimaryLabel: 'Diagnostic 60 sec',
    ctaPrimaryHref: '/outils',
    ctaSecondaryLabel: 'Découvrir nos 3 pôles',
    ctaSecondaryHref: '/a-propos',
    proofLabel: 'pôles intégrés',
    proofValue: 3,
    proofSuffix: '',
  },
  render: HeroPremium,
};
